import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import LeadDetailPanel from './LeadDetailPanel';
import LeadScoreRing from './LeadScoreRing';
import MoveToBankCRMDialog from './nurture/MoveToBankCRMDialog';
import StartOnboardingDialog from './onboarding/StartOnboardingDialog';
import { ArrowRight, Building2, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';

/**
 * Slide-over wrapper around the existing LeadDetailPanel.
 *
 * The redesign adds a sticky top action bar with a primary "Move to Bank CRM"
 * CTA (gradient pill) that opens a confirmation dialog. The dialog calls
 * POST /api/nurture/leads/{id}/graduate which marks the lead as graduated.
 */
const LeadDetailSheet = ({
  lead, open, onClose, onUpdate, options, templates, authToken,
}) => {
  const [showHandoff, setShowHandoff] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);
  const navigate = useNavigate();

  const handleGraduated = (updated) => {
    // Push the updated lead back to the parent (status: graduated). The parent
    // refreshes its leads list and drops the graduated row from the active view.
    if (onUpdate) onUpdate(updated);
    onClose?.();
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl xl:max-w-3xl overflow-y-auto bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 p-0 data-[state=open]:duration-300"
          data-testid="lead-drawer"
        >
          <SheetHeader className="px-6 pt-6 pb-3 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {lead && (
                  <LeadScoreRing
                    size={52}
                    score={lead.lead_score || 0}
                    name={lead.name || '?'}
                    temperature={lead.lead_temperature || 'Cold'}
                  />
                )}
                <div className="min-w-0">
                  <SheetTitle className="font-display text-xl font-bold tracking-tight text-left text-slate-900 dark:text-slate-50 truncate">
                    {lead?.name || 'Lead'}
                  </SheetTitle>
                  <div className="text-xs text-slate-500 dark:text-slate-400 font-workspace-body truncate">
                    {lead?.lead_number} · {lead?.lead_temperature || 'Cold'} · score {lead?.lead_score ?? '—'}
                  </div>
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowOnboard(true)}
                disabled={!lead}
                className="rounded-full px-3 py-2 font-semibold border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                data-testid="lead-drawer-start-onboarding"
                title="Send this lead their secure Client Portal link"
              >
                <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                <span className="hidden sm:inline">{lead?.onboarding_application_id ? 'Client Portal' : 'Start Client Portal'}</span>
                <span className="sm:hidden">Portal</span>
              </Button>
              <Button
                size="sm"
                onClick={() => setShowHandoff(true)}
                disabled={!lead || lead.status === 'graduated'}
                className={cn(
                  'flex-shrink-0 rounded-full bg-gradient-to-r from-crm-navy to-crm-blue text-white px-4 py-2 font-semibold shadow-md hover:shadow-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
                )}
                data-testid="lead-drawer-move-to-bank"
                title="Hand this lead off to your bank CRM"
              >
                <Building2 className="w-3.5 h-3.5 mr-1.5" />
                <span className="hidden sm:inline">Move to Bank CRM</span>
                <span className="sm:hidden">Bank CRM</span>
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
              </div>
            </div>
            {lead?.onboarding_application_id && (
              <button type="button" onClick={() => navigate(`/onboarding/${lead.onboarding_application_id}`)} data-testid="lead-drawer-open-onboarding"
                className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-indigo-50 border border-indigo-200 text-[11px] font-semibold text-indigo-700 w-fit hover:bg-indigo-100">
                <ShieldCheck className="w-3 h-3" /> Client Portal {lead.onboarding_status ? `· ${String(lead.onboarding_status).replace(/_/g, ' ')}` : ''} — open workbench
              </button>
            )}
            {lead?.status === 'graduated' && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 w-fit">
                <Building2 className="w-3 h-3" />
                Graduated to {lead.handoff_target || 'Bank CRM'}
                {lead.graduated_at && (
                  <span className="text-emerald-600/70 dark:text-emerald-500/70 font-normal ml-1">
                    · {new Date(lead.graduated_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}
          </SheetHeader>

          <div className="px-2 sm:px-4 pb-6">
            {lead && (
              <LeadDetailPanel
                lead={lead}
                onClose={onClose}
                onUpdate={onUpdate}
                options={options}
                templates={templates}
                authToken={authToken}
                mode="sheet"
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      <MoveToBankCRMDialog
        open={showHandoff}
        onOpenChange={setShowHandoff}
        lead={lead}
        token={authToken}
        onGraduated={handleGraduated}
      />
      <StartOnboardingDialog
        open={showOnboard}
        onOpenChange={setShowOnboard}
        initialSource={lead ? { type: 'nurture_lead', id: lead.id } : null}
        onCreated={(id) => { if (onUpdate && lead) onUpdate({ ...lead, onboarding_application_id: id, onboarding_status: 'invited' }); }}
      />
    </>
  );
};

export default LeadDetailSheet;
